import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Table,
  Badge,
  Spinner,
  Alert,
  Button,
} from "reactstrap";
import { format } from "date-fns";
import { toast } from "react-toastify";
import CampaignSchedule from "../../services/OrderingServices/CampaignSchedule";
import EditScheduleModal from "./EditScheduleModal";
import DeleteModal from "../../Components/Common/DeleteModal";
import "./CampaignSchedule.css";
import BreadCrumb from "../../Components/Common/BreadCrumb";

const CampaignScheduleList = () => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [scheduleToDelete, setScheduleToDelete] = useState(null);

  useEffect(() => {
    fetchSchedules();
  }, []);

  const fetchSchedules = async () => {
    try {
      setLoading(true);
      const data = await CampaignSchedule.list();
      setSchedules(data);
      setError(null);
    } catch (err) {
      setError(err.message || "Failed to fetch campaign schedules");
    } finally {
      setLoading(false);
    }
  };

  const getActiveDays = (schedule) => {
    const days = [];
    if (schedule.sunday) days.push("Sun");
    if (schedule.monday) days.push("Mon");
    if (schedule.tuesday) days.push("Tue");
    if (schedule.wednesday) days.push("Wed");
    if (schedule.thursday) days.push("Thu");
    if (schedule.friday) days.push("Fri");
    if (schedule.saturday) days.push("Sat");

    return days.length > 0 ? days.join(", ") : "None";
  };

  const formatDateRange = (startDate, endDate) => {
    try {
      const start = format(new Date(startDate), "MMM d, yyyy");
      const end = endDate
        ? format(new Date(endDate), "MMM d, yyyy")
        : "Ongoing";
      return `${start} → ${end}`;
    } catch (err) {
      return "Invalid date";
    }
  };

  const formatTime = (timeString) => {
    try {
      if (!timeString) return "N/A";
      // timeString format: "12:00:00"
      const [hours, minutes] = timeString.split(":");
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    } catch (err) {
      return timeString;
    }
  };

  const getTimeRange = (startTime, endTime) => {
    if (startTime === endTime) {
      return formatTime(startTime);
    }
    return `${formatTime(startTime)} - ${formatTime(endTime)}`;
  };

  const handleEdit = (schedule) => {
    setSelectedSchedule(schedule);
    setEditModalOpen(true);
  };

  const handleUpdate = async (id, data) => {
    try {
      await CampaignSchedule.update(id, data);
      await fetchSchedules();
      toast.success("Campaign schedule updated successfully");
    } catch (error) {
      toast.error(error.message || "Failed to update schedule");
      throw error;
    }
  };

  const handleDelete = async () => {
    if (!scheduleToDelete) return;

    try {
      await CampaignSchedule.delete(scheduleToDelete.id);
      await fetchSchedules();
      toast.success("Campaign schedule deleted successfully");
      setDeleteModalOpen(false);
      setScheduleToDelete(null);
    } catch (error) {
      toast.error(error.message || "Failed to delete schedule");
      setDeleteModalOpen(false);
      setScheduleToDelete(null);
    }
  };

  const confirmDelete = (schedule) => {
    setScheduleToDelete(schedule);
    setDeleteModalOpen(true);
  };

  const handleToggleStatus = async (schedule) => {
    const action = schedule.isActive ? "deactivate" : "activate";
    const confirmed = window.confirm(`Do you want to ${action} this schedule?`);

    if (confirmed) {
      try {
        const payload = {
          campaignId: schedule.campaignId,
          startDate: schedule.startDate,
          endDate: schedule.endDate,
          sunday: schedule.sunday,
          monday: schedule.monday,
          tuesday: schedule.tuesday,
          wednesday: schedule.wednesday,
          thursday: schedule.thursday,
          friday: schedule.friday,
          saturday: schedule.saturday,
          startTime: schedule.startTime,
          endTime: schedule.endTime,
          isActive: !schedule.isActive,
        };

        await CampaignSchedule.update(schedule.id, payload);
        await fetchSchedules();
        toast.success(`Schedule ${action}d successfully`);
      } catch (error) {
        toast.error(error.message || `Failed to ${action} schedule`);
      }
    }
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <BreadCrumb title="Campaign Schedule" pageTitle="Campaigns" />
      </div>
      <Container fluid>
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h4 className="card-title mb-1">Campaign Schedules</h4>
                    <p className="text-muted mb-0">
                      View and manage recurring campaign schedules
                    </p>
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={fetchSchedules}
                    disabled={loading}
                  >
                    <i className="mdi mdi-refresh me-1"></i>
                    Refresh
                  </button>
                </div>

                {error && (
                  <Alert color="danger" className="mb-3">
                    {error}
                  </Alert>
                )}

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                    <p className="mt-3 text-muted">Loading schedules...</p>
                  </div>
                ) : schedules.length === 0 ? (
                  <div className="text-center py-5">
                    <div className="mb-3">
                      <svg
                        width="64"
                        height="64"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        className="text-muted"
                      >
                        <rect
                          x="3"
                          y="4"
                          width="18"
                          height="18"
                          rx="2"
                          ry="2"
                        />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    </div>
                    <h5 className="text-muted">No Scheduled Campaigns</h5>
                    <p className="text-muted">
                      Campaign schedules will appear here once created
                    </p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <Table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>SN</th>
                          <th>Campaign</th>
                          <th>Date Range</th>
                          <th>Recurring Days</th>
                          <th>Time</th>
                          <th>Status</th>
                          <th>Created</th>
                          <th className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {schedules.map((schedule) => (
                          <tr key={schedule.id}>
                            <td>{schedules.indexOf(schedule) + 1}</td>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="flex-shrink-0 me-3">
                                  <div
                                    className="avatar-sm rounded-circle bg-soft-primary d-flex align-items-center justify-content-center"
                                    style={{
                                      backgroundColor: "#f0f7ff",
                                      width: "40px",
                                      height: "40px",
                                    }}
                                  >
                                    <i
                                      className="mdi mdi-calendar-clock"
                                      style={{
                                        fontSize: "20px",
                                        color: "#0d6efd",
                                      }}
                                    ></i>
                                  </div>
                                </div>
                                <div className="flex-grow-1">
                                  <h6 className="mb-1">
                                    {schedule.campaignName}
                                  </h6>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <i className="mdi mdi-calendar-range text-primary me-2"></i>
                                <span>
                                  {formatDateRange(
                                    schedule.startDate,
                                    schedule.endDate,
                                  )}
                                </span>
                              </div>
                            </td>
                            <td>
                              <div className="d-flex flex-wrap gap-1">
                                {getActiveDays(schedule)
                                  .split(", ")
                                  .map((day, idx) => (
                                    <Badge
                                      key={idx}
                                      color="primary"
                                      className="bg-soft-primary text-primary"
                                      style={{
                                        fontWeight: "500",
                                        fontSize: "11px",
                                        padding: "4px 8px",
                                      }}
                                    >
                                      {day}
                                    </Badge>
                                  ))}
                              </div>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <i className="mdi mdi-clock-outline text-muted me-2"></i>
                                <span className="text-nowrap">
                                  {getTimeRange(
                                    schedule.startTime,
                                    schedule.endTime,
                                  )}
                                </span>
                              </div>
                            </td>
                            <td>
                              {schedule.isActive ? (
                                <Badge color="success" pill>
                                  <i className="mdi mdi-check-circle me-1"></i>
                                  Active
                                </Badge>
                              ) : (
                                <Badge color="secondary" pill>
                                  <i className="mdi mdi-pause-circle me-1"></i>
                                  Inactive
                                </Badge>
                              )}
                            </td>
                            <td className="text-muted">
                              {format(
                                new Date(schedule.createdAt),
                                "MMM d, yyyy",
                              )}
                            </td>
                            <td>
                              <div className="d-flex justify-content-center gap-2">
                                <Button
                                  color="btn btn-soft-primary"
                                  className="btn-sm"
                                  onClick={() => handleEdit(schedule)}
                                  title="Edit"
                                >
                                  <i className="ri-edit-line" />
                                </Button>

                                {/* <Button
                                  color="info"
                                  size="sm"
                                  onClick={() => handleEdit(schedule)}
                                  title="Edit"
                                >
                                  <i className="mdi mdi-pencil"></i>
                                </Button> */}
                                {/* <Button
                                  color={
                                    schedule.isActive
                                      ? "btn btn-soft-warning"
                                      : "btn btn-soft-success"
                                  }
                                  size="sm"
                                  onClick={() => handleToggleStatus(schedule)}
                                  title={
                                    schedule.isActive
                                      ? "Deactivate"
                                      : "Activate"
                                  }
                                >
                                  <i
                                    className={`${
                                      schedule.isActive
                                        ? "ri-pause-fill"
                                        : "ri-play-fill"
                                    }`}
                                  ></i>
                                </Button> */}
                                {/* <Button
                                  color="danger"
                                  size="sm"12
                                  onClick={() => confirmDelete(schedule)}
                                  title="Delete"
                                >
                                  <i className="mdi mdi-delete"></i>
                                </Button> */}

                                <Button
                                  color="btn btn-soft-danger"
                                  className="btn-sm"
                                  onClick={() => confirmDelete(schedule)}
                                  title="Delete"
                                >
                                  <i className="ri-delete-bin-line" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}

                {!loading && schedules.length > 0 && (
                  <div className="mt-3 text-muted">
                    <small>
                      Showing {schedules.length} schedule
                      {schedules.length !== 1 ? "s" : ""}
                    </small>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Edit Modal */}
      <EditScheduleModal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedSchedule(null);
        }}
        schedule={selectedSchedule}
        onUpdate={handleUpdate}
      />

      {/* Delete Modal */}
      <DeleteModal
        isOpen={deleteModalOpen}
        toggle={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Delete Schedule?"
        body={`Are you sure you want to delete the schedule for "${scheduleToDelete?.campaignName}"? This action cannot be undone.`}
      />
    </div>
  );
};

export default CampaignScheduleList;
